import { useEffect, useState } from "react";
import { Check, Copy, Truck } from "lucide-react";
import { toast } from "@aurelia/ui";
import type { OrderStatus } from "@aurelia/types";

export function TrackingNumber({ trackingNumber, status }: { trackingNumber: string | null; status: OrderStatus }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!trackingNumber) {
    if (status === "CANCELLED" || status === "DELIVERED") return null;
    return (
      <div className="flex items-start gap-2 text-sm text-ink-muted">
        <Truck size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>{status === "SHIPPED" ? "Tracking details will be added shortly." : "Tracking will be available after shipment."}</span>
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(trackingNumber);
      setCopied(true);
    } catch {
      toast("Couldn't copy the tracking number. Please try again.", "danger");
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Tracking number</p>
        <p className="mt-0.5 break-all font-mono text-sm font-medium text-ink" title={trackingNumber}>{trackingNumber}</p>
      </div>
      <button
        type="button"
        onClick={copy}
        aria-label="Copy tracking number"
        className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-full border border-border bg-surface px-4 text-sm font-medium transition-colors hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40 sm:w-auto"
      >
        {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
        <span aria-live="polite">{copied ? "Copied" : "Copy"}</span>
      </button>
    </div>
  );
}
